import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

// Mirrors the prototype's `.collapsible`/`.collapse-toggle` system (every
// content card in Usage & Billing gets a working collapse chevron — see
// ARCHITECTURE_DECISIONS.md). Card owns the collapsed state and shares it
// with CardHeader (renders the chevron) and CardBody (hides itself) via
// context, since they're siblings rather than parent/child in call sites.
const CollapseContext = createContext<{ collapsed: boolean; toggle: () => void } | null>(null)

export function Card({
  children,
  className = '',
  collapsible = false,
}: Readonly<{
  children: ReactNode
  className?: string
  collapsible?: boolean
}>) {
  const [collapsed, setCollapsed] = useState(false)
  const toggle = useCallback(() => setCollapsed((c) => !c), [])
  // Context value must be a stable reference across renders that don't
  // actually change `collapsed` — otherwise every Card re-render (e.g. from
  // an unrelated parent state change) would re-render CardHeader/CardBody
  // too (Sonar typescript:S6481).
  const value = useMemo(() => ({ collapsed, toggle }), [collapsed, toggle])
  // `data-collapsed` lets a caller size the card by its state — a card that
  // grows to fill the page shrinks back to its header when collapsed
  // (`data-[collapsed=true]:flex-none`, User Limits' Individual card, MS-996).
  const content = (
    <div
      data-collapsed={collapsible ? collapsed : undefined}
      className={`bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] shadow-[var(--shadow-sm)] ${className}`}
    >
      {children}
    </div>
  )
  if (!collapsible) return content
  return <CollapseContext.Provider value={value}>{content}</CollapseContext.Provider>
}

export function CardHeader({
  title,
  subtitle,
  actions,
}: Readonly<{
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
}>) {
  const collapse = useContext(CollapseContext)
  const { t } = useTranslation()
  return (
    <div className="px-6 pt-4 pb-3 relative">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-[14px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
            {title}
          </h3>
          {subtitle && <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {collapse && (
            <button
              type="button"
              onClick={collapse.toggle}
              title={collapse.collapsed ? t('common.expand') : t('common.collapse')}
              aria-label={collapse.collapsed ? t('common.expand') : t('common.collapse')}
              className="text-[var(--text-tertiary)] p-1 flex items-center justify-center transition-transform"
              style={{ transform: collapse.collapsed ? 'rotate(-90deg)' : undefined }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {/* The 1px rule under the header (the prototype's `.card-header::after`),
          hidden but still taking its space on a collapsed card
          (`.card.collapsed .card-header::after { visibility: hidden }`). */}
      <div
        data-testid="card-header-divider"
        className={`h-px bg-[var(--border-light)] mt-2.5 ${collapse?.collapsed ? 'invisible' : ''}`}
      />
    </div>
  )
}

export function CardBody({
  children,
  className = '',
}: Readonly<{ children: ReactNode; className?: string }>) {
  const collapse = useContext(CollapseContext)
  if (collapse?.collapsed) return null
  // The prototype's `.card-body { padding: 24px }` pads all four sides
  // equally — pt-6 included, not just px-6/pb-6. Without it, content sits
  // flush against CardHeader's divider whenever there's no legend/label
  // above it to fake the gap (most visible on the Mambu Agents Usage tab,
  // which has no invocation-type legend at all).
  return <div className={`px-6 pb-6 pt-6 ${className}`}>{children}</div>
}
